import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Paperclip, X } from "lucide-react";
import axiosService from "../../core/services/axios-service";
import localStorageService from "../../core/services/local-storage-service";
import { ApiRoutes } from "../../core/dictionary/api-url";
import {
  orgranizationDepartment,
  orgranizationRole,
} from "../../core/dictionary/orginazation-dropdown";
import { drillKeyOutcomes } from "../../core/dictionary/drill-dropdown";
import ToastService from "../../core/services/toaster-service";
import "../../assets/scss/pre-team-course-survey.scss";

// Uploads go to S3 server-side, so allow well past the default 10s axios timeout.
const UPLOAD_TIMEOUT_MS = 120000;

const initialForm = {
  fullName: "",
  email: "",
  licenseNumber: "",
  healthSystem: "",
  role: "",
  unit: "",
  drillDate: "",
  numberOfParticipants: "",
  numberOfDrillsDone: "",
  keyOutcomes: [],
  notes: "",
};

export default function SubmitDrill() {
  const user = localStorageService.getUserDetails();
  const [form, setForm] = useState({
    ...initialForm,
    fullName: (user && (user.name || user.fullName || user.firstName)) || "",
    email: (user && user.email) || "",
  });
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const navigate = useNavigate();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  // Key Outcomes is multi-select — toggle the value in/out of the array.
  const handleOutcomeToggle = (value) => {
    setForm((prev) => ({
      ...prev,
      keyOutcomes: prev.keyOutcomes.includes(value)
        ? prev.keyOutcomes.filter((v) => v !== value)
        : [...prev.keyOutcomes, value],
    }));
  };

  const handleFileChange = (e) => {
    setFiles((prev) => [...prev, ...Array.from(e.target.files || [])]);
    // Reset so re-picking the same file still fires onChange.
    e.target.value = "";
  };

  const handleFileRemove = (index) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const validate = () => {
    if (!form.email.trim()) return "Email is required.";
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(form.email)) return "Please enter a valid email address.";
    if (form.numberOfParticipants && Number(form.numberOfParticipants) < 0)
      return "Number of Participants cannot be negative.";
    if (form.numberOfDrillsDone && Number(form.numberOfDrillsDone) < 0)
      return "Number of Drills Done cannot be negative.";
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }

    setLoading(true);
    try {
      const payload = new FormData();
      // Optional text fields are only appended when filled so the API
      // doesn't record empty strings.
      const appendIfSet = (key, value) => {
        if (value !== "" && value !== null && value !== undefined) {
          payload.append(key, value);
        }
      };

      appendIfSet("Full Name", form.fullName.trim());
      payload.append("Email", form.email.trim());
      appendIfSet("License #", form.licenseNumber.trim());
      appendIfSet("Health System", form.healthSystem.trim());
      appendIfSet("Role", form.role);
      appendIfSet("Unit", form.unit);
      appendIfSet("Date of Drill", form.drillDate);
      appendIfSet("Number of Participants", form.numberOfParticipants);
      appendIfSet("Number of Drills Done", form.numberOfDrillsDone);
      appendIfSet("Notes / Videos", form.notes.trim());
      // Repeated field — one entry per selected outcome.
      form.keyOutcomes.forEach((outcome) => {
        payload.append("Key Outcomes (check all that apply)", outcome);
      });
      files.forEach((file) => payload.append("file", file));

      const res = await axiosService.post(
        ApiRoutes.CODEPREP_DRILL_SUBMISSION,
        payload,
        {
          headers: { "Content-Type": "multipart/form-data" },
          timeout: UPLOAD_TIMEOUT_MS,
        }
      );

      if (res.data && res.data.success) {
        ToastService.success(
          res.data.message || "Drill submitted successfully!"
        );
        setSubmitted(true);
      } else {
        const msg =
          res.data?.message || "Failed to submit drill. Please try again.";
        setError(msg);
        ToastService.error(msg);
      }
    } catch (err) {
      const msg =
        err?.response?.data?.message || err?.message || "Server error. Please try again.";
      setError(msg);
      ToastService.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="survey-page">
      {/* Header */}
      <header className="survey-header">
        <img
          src="https://res.cloudinary.com/dhhzpdckr/image/upload/v1758128880/rescuernlogo_dsi3mf.png"
          alt="RescueRn Logo"
          className="survey-header__logo"
        />
      </header>

      {/* Main content */}
      <main className="survey-main">
        {submitted ? (
          <div className="survey-thankyou">
            <div className="survey-thankyou__icon">✓</div>
            <h2 className="survey-thankyou__title">Thank You!</h2>
            <p className="survey-thankyou__message">
              Your drill has been submitted successfully. Thank you for keeping
              your team CodePRep ready.
            </p>
            <button
              type="button"
              className="survey-submit-btn survey-submit-btn--inline"
              onClick={() => navigate("/dashboard")}
            >
              Back to Dashboard
            </button>
          </div>
        ) : (
          <div className="survey-card">
            <div className="survey-card__intro">
              <h1 className="survey-card__title">Submit Drill</h1>
              <p className="survey-card__subtitle">
                Record the details of the CodePRep drill your team completed.
              </p>
            </div>

            <form onSubmit={handleSubmit} noValidate>
              {/* Section 1: Submitter Information */}
              <div className="survey-section">
                <h3 className="survey-section__heading">
                  Submitter Information
                </h3>

                <div className="survey-row">
                  <div className="survey-field">
                    <label className="survey-label" htmlFor="fullName">
                      Full Name
                    </label>
                    <input
                      id="fullName"
                      type="text"
                      name="fullName"
                      className="survey-input"
                      placeholder="Enter your full name"
                      value={form.fullName}
                      onChange={handleChange}
                    />
                  </div>
                  <div className="survey-field">
                    <label className="survey-label" htmlFor="email">
                      Email <span className="survey-required">*</span>
                    </label>
                    <input
                      id="email"
                      type="email"
                      name="email"
                      className="survey-input"
                      placeholder="Enter your email"
                      value={form.email}
                      onChange={handleChange}
                      required
                    />
                  </div>
                </div>

                <div className="survey-row">
                  <div className="survey-field">
                    <label className="survey-label" htmlFor="licenseNumber">
                      License #
                    </label>
                    <input
                      id="licenseNumber"
                      type="text"
                      name="licenseNumber"
                      className="survey-input"
                      placeholder="Enter your license number"
                      value={form.licenseNumber}
                      onChange={handleChange}
                    />
                  </div>
                  <div className="survey-field">
                    <label className="survey-label" htmlFor="healthSystem">
                      Health System
                    </label>
                    <input
                      id="healthSystem"
                      type="text"
                      name="healthSystem"
                      className="survey-input"
                      placeholder="Enter your health system"
                      value={form.healthSystem}
                      onChange={handleChange}
                    />
                  </div>
                </div>

                <div className="survey-row">
                  <div className="survey-field">
                    <label className="survey-label" htmlFor="role">
                      Role
                    </label>
                    <select
                      id="role"
                      name="role"
                      className="survey-select"
                      value={form.role}
                      onChange={handleChange}
                    >
                      <option value="">Select Role</option>
                      {orgranizationRole.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="survey-field">
                    <label className="survey-label" htmlFor="unit">
                      Unit
                    </label>
                    <select
                      id="unit"
                      name="unit"
                      className="survey-select"
                      value={form.unit}
                      onChange={handleChange}
                    >
                      <option value="">Select Unit</option>
                      {orgranizationDepartment.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              {/* Section 2: Drill Details */}
              <div className="survey-section">
                <h3 className="survey-section__heading">Drill Details</h3>

                <div className="survey-row">
                  <div className="survey-field">
                    <label className="survey-label" htmlFor="drillDate">
                      Date of Drill
                    </label>
                    <input
                      id="drillDate"
                      type="date"
                      name="drillDate"
                      className="survey-input"
                      value={form.drillDate}
                      onChange={handleChange}
                    />
                  </div>
                  <div className="survey-field">
                    <label
                      className="survey-label"
                      htmlFor="numberOfParticipants"
                    >
                      Number of Participants
                    </label>
                    <input
                      id="numberOfParticipants"
                      type="number"
                      min="0"
                      name="numberOfParticipants"
                      className="survey-input"
                      placeholder="e.g. 10"
                      value={form.numberOfParticipants}
                      onChange={handleChange}
                    />
                  </div>
                </div>

                <div className="survey-field survey-field--full">
                  <label className="survey-label" htmlFor="numberOfDrillsDone">
                    Number of Drills Done
                  </label>
                  <input
                    id="numberOfDrillsDone"
                    type="number"
                    min="0"
                    name="numberOfDrillsDone"
                    className="survey-input"
                    placeholder="e.g. 3"
                    value={form.numberOfDrillsDone}
                    onChange={handleChange}
                  />
                </div>

                <div className="survey-field survey-field--full">
                  <label className="survey-label">
                    Key Outcomes (check all that apply)
                  </label>
                  <div className="survey-checkbox-group">
                    {drillKeyOutcomes.map((opt) => (
                      <label key={opt.value} className="survey-checkbox-label">
                        <input
                          type="checkbox"
                          className="survey-checkbox"
                          value={opt.value}
                          checked={form.keyOutcomes.includes(opt.value)}
                          onChange={() => handleOutcomeToggle(opt.value)}
                        />
                        {opt.label}
                      </label>
                    ))}
                  </div>
                </div>

                <div className="survey-field survey-field--full">
                  <label className="survey-label" htmlFor="notes">
                    Notes / Videos
                  </label>
                  <textarea
                    id="notes"
                    name="notes"
                    rows={4}
                    className="survey-input survey-textarea"
                    placeholder="Share any additional notes or links to drill videos"
                    value={form.notes}
                    onChange={handleChange}
                  />
                </div>
              </div>

              {/* Section 3: Attachments */}
              <div className="survey-section">
                <h3 className="survey-section__heading">Attachments</h3>
                <p className="survey-section__description">
                  Optionally attach photos, videos or documents from the drill.
                </p>

                <div className="survey-field survey-field--full">
                  <label className="survey-file-drop" htmlFor="drillFiles">
                    <Paperclip size={18} />
                    <span>Choose files</span>
                    <input
                      id="drillFiles"
                      type="file"
                      multiple
                      className="survey-file-input"
                      onChange={handleFileChange}
                    />
                  </label>

                  {files.length > 0 && (
                    <ul className="survey-file-list">
                      {files.map((file, index) => (
                        <li
                          key={`${file.name}-${index}`}
                          className="survey-file-item"
                        >
                          <span className="survey-file-item__name">
                            {file.name}
                          </span>
                          <button
                            type="button"
                            className="survey-file-item__remove"
                            onClick={() => handleFileRemove(index)}
                            aria-label={`Remove ${file.name}`}
                          >
                            <X size={16} />
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>

              {error && <div className="survey-error">{error}</div>}

              <button
                type="submit"
                className="survey-submit-btn"
                disabled={loading}
              >
                {loading ? "Submitting..." : "Submit Drill"}
              </button>
            </form>
          </div>
        )}
      </main>
    </div>
  );
}
